import { Routes } from '@angular/router';

import { RouteString } from '@lyra/app/core/enums/route-string';
import { questionnaireResolver } from '@lyra/app/core/resolvers/questionnaire.resolver';
import { IMainPageConfig } from '@lyra/app/pages/main.config';
import { IOrderPageConfig } from '@lyra/app/pages/order/order.component';
import { provideQuestionnaireStore } from '@lyra/app/pages/questionnaire/core/store';
import { IQuestionnaireConfig } from '@lyra/app/pages/questionnaire/questionnaire.config';

import { orderStatusGuard } from '../val-quiz/core/guards/order-status.guard';
import { aliRealUserGuard, aliTokenGuard, clearAliSessionResolver } from './ali-session.guard';

/**
 * Routes for the ali-pages section, lazy-loaded from app.routes at the lang root.
 * Both landing variants live here, each as a section shell (AliPagesComponent = `<router-outlet>` +
 * section styles) with its own children:
 *   en/ali-a (variant A landing) · en/ali-b (variant B landing)
 *
 * Each variant provides the questionnaire store so the post-checkout mini-flow
 * (offer → order → login → questionnaire → survey-passed) works, mirroring val-quiz. The shared
 * OrderComponent / LoginComponent / QuestionnaireComponent / SurveyPassedComponent are reused, with
 * the per-variant redirects passed through route `data`.
 *
 * Everything past the offer is gated by {@link aliTokenGuard} (needs the offer's session token, so the
 * funnel can't be deep-linked); the questionnaire additionally needs {@link aliRealUserGuard} (a real
 * logged-in user). survey-passed clears the session so the flow can't be re-entered.
 */
export const aliPagesRoutes: Routes = [
  {
    path: `${RouteString.ALI_PAGE_VARIANT_A}`,
    loadComponent: () => import('./ali-pages.component').then((m) => m.AliPagesComponent),
    providers: [provideQuestionnaireStore()],
    children: [
      {
        path: ``,
        data: {
          config: {
            assessmentPath: [RouteString.ALI_PAGE_VARIANT_A, RouteString.OFFER],
            homePath: RouteString.ALI_PAGE_VARIANT_A,
            showLogin: false,
            hideLimitedOffer: true,
          } satisfies IMainPageConfig,
        },
        loadComponent: () => import('../main.component').then((m) => m.MainPageComponent),
      },
      {
        // Education-led offer page (Figma 5584-28013) — en/ali-a/offer.
        // questionnaireResolver registers an anonymous (temporary) user so the checkout below has a session.
        path: `${RouteString.OFFER}`,
        data: { config: { homePath: RouteString.ALI_PAGE_VARIANT_A } },
        resolve: { _user: questionnaireResolver },
        loadComponent: () => import('./offer-a/offer-page.component').then((m) => m.AliOfferPageComponent),
      },
      {
        // Checkout reached from the offer's pricing CTA — reuses the shared OrderComponent (val-quiz flow).
        path: `${RouteString.ORDER}`,
        canActivate: [
          aliTokenGuard(RouteString.ALI_PAGE_VARIANT_A),
          orderStatusGuard([RouteString.ALI_PAGE_VARIANT_A, RouteString.LOGIN]),
        ],
        data: { config: { homePath: RouteString.ALI_PAGE_VARIANT_A, paymentType: 'ali-flow' } satisfies IOrderPageConfig },
        loadComponent: () => import('../order/order.component').then((m) => m.OrderComponent),
      },
      {
        // Post-checkout login — reuses the shared login page; confirm-code success continues to the questionnaire.
        path: `${RouteString.LOGIN}`,
        canActivate: [aliTokenGuard(RouteString.ALI_PAGE_VARIANT_A)],
        data: {
          homePath: RouteString.ALI_PAGE_VARIANT_A,
          confirmRedirect: [RouteString.ALI_PAGE_VARIANT_A, RouteString.QUESTIONNAIRE],
        },
        loadComponent: () => import('../val-quiz/login/login.component').then((m) => m.LoginComponent),
      },
      {
        // Post-login questionnaire — the vmain mini-flow; 'ali-flow-a' lands on this variant's survey-passed.
        // Needs the offer token AND a real logged-in user.
        path: `${RouteString.QUESTIONNAIRE}`,
        canActivate: [aliTokenGuard(RouteString.ALI_PAGE_VARIANT_A), aliRealUserGuard(RouteString.ALI_PAGE_VARIANT_A)],
        data: {
          config: {
            homePath: RouteString.ALI_PAGE_VARIANT_A,
            assessmentPath: RouteString.OFFER,
            type: 'ali-flow-a',
          } satisfies IQuestionnaireConfig,
        },
        loadComponent: () => import('../questionnaire/questionnaire.component').then((m) => m.QuestionnaireComponent),
      },
      {
        // Terminal survey-passed page — reuses the shared component; clears the session on arrival.
        path: `${RouteString.SURVEY_PASSED}`,
        canActivate: [aliTokenGuard(RouteString.ALI_PAGE_VARIANT_A)],
        resolve: { _clearSession: clearAliSessionResolver },
        data: { homePath: RouteString.ALI_PAGE_VARIANT_A },
        loadComponent: () => import('../val-quiz/survey-passed/survey-passed.component').then((m) => m.SurveyPassedComponent),
      },
    ],
  },
  {
    path: `${RouteString.ALI_PAGE_VARIANT_B}`,
    loadComponent: () => import('./ali-pages.component').then((m) => m.AliPagesComponent),
    providers: [provideQuestionnaireStore()],
    children: [
      {
        path: ``,
        data: {
          config: {
            assessmentPath: [RouteString.ALI_PAGE_VARIANT_B, RouteString.OFFER],
            homePath: RouteString.ALI_PAGE_VARIANT_B,
            showLogin: false,
            hideLimitedOffer: true,
          } satisfies IMainPageConfig,
        },
        loadComponent: () => import('../main.component').then((m) => m.MainPageComponent),
      },
      {
        // Price-first offer page — same sections/copy as Variant A, reordered — en/ali-b/offer.
        // questionnaireResolver registers an anonymous (temporary) user so the checkout below has a session.
        path: `${RouteString.OFFER}`,
        data: { config: { homePath: RouteString.ALI_PAGE_VARIANT_B } },
        resolve: { _user: questionnaireResolver },
        loadComponent: () => import('./offer-b/offer-page.component').then((m) => m.AliOfferBPageComponent),
      },
      {
        // Checkout reached from the offer's pricing CTA — reuses the shared OrderComponent (val-quiz flow).
        path: `${RouteString.ORDER}`,
        canActivate: [
          aliTokenGuard(RouteString.ALI_PAGE_VARIANT_B),
          orderStatusGuard([RouteString.ALI_PAGE_VARIANT_B, RouteString.LOGIN]),
        ],
        data: { config: { homePath: RouteString.ALI_PAGE_VARIANT_B, paymentType: 'ali-flow' } satisfies IOrderPageConfig },
        loadComponent: () => import('../order/order.component').then((m) => m.OrderComponent),
      },
      {
        // Post-checkout login — reuses the shared login page; confirm-code success continues to the questionnaire.
        path: `${RouteString.LOGIN}`,
        canActivate: [aliTokenGuard(RouteString.ALI_PAGE_VARIANT_B)],
        data: {
          homePath: RouteString.ALI_PAGE_VARIANT_B,
          confirmRedirect: [RouteString.ALI_PAGE_VARIANT_B, RouteString.QUESTIONNAIRE],
        },
        loadComponent: () => import('../val-quiz/login/login.component').then((m) => m.LoginComponent),
      },
      {
        // Post-login questionnaire — the vmain mini-flow; 'ali-flow-b' lands on this variant's survey-passed.
        // Needs the offer token AND a real logged-in user.
        path: `${RouteString.QUESTIONNAIRE}`,
        canActivate: [aliTokenGuard(RouteString.ALI_PAGE_VARIANT_B), aliRealUserGuard(RouteString.ALI_PAGE_VARIANT_B)],
        data: {
          config: {
            homePath: RouteString.ALI_PAGE_VARIANT_B,
            assessmentPath: RouteString.OFFER,
            type: 'ali-flow-b',
          } satisfies IQuestionnaireConfig,
        },
        loadComponent: () => import('../questionnaire/questionnaire.component').then((m) => m.QuestionnaireComponent),
      },
      {
        // Terminal survey-passed page — reuses the shared component; clears the session on arrival.
        path: `${RouteString.SURVEY_PASSED}`,
        canActivate: [aliTokenGuard(RouteString.ALI_PAGE_VARIANT_B)],
        resolve: { _clearSession: clearAliSessionResolver },
        data: { homePath: RouteString.ALI_PAGE_VARIANT_B },
        loadComponent: () => import('../val-quiz/survey-passed/survey-passed.component').then((m) => m.SurveyPassedComponent),
      },
    ],
  },
];
